import React, { useMemo } from 'react';
import { Row, Col, Tag, Space, Progress, Tooltip } from 'antd';
import {
    CommentOutlined,
    StarFilled,
    MessageOutlined,
    CameraOutlined,
    AlertFilled,
    SmileOutlined,
    FrownOutlined,
    RobotOutlined,
    LoadingOutlined,
    HistoryOutlined,
    RiseOutlined,
    FallOutlined,
    PieChartOutlined,
} from '@ant-design/icons';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    ReferenceLine,
    RadarChart,
    Radar,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
} from 'recharts';

// 별점별 분석 탭 컴포넌트
const StarAnalysisTabs = ({ starAnalysis }) => {
    const [active, setActive] = React.useState('5');
    const stars = ['5', '4', '3', '2', '1'];
    const data = starAnalysis[active];

    return (
        <div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
                {stars.map(s => {
                    const has = !!starAnalysis[s];
                    const sel = active === s;
                    return (
                        <button
                            key={s}
                            onClick={() => has && setActive(s)}
                            style={{
                                padding: '6px 16px',
                                borderRadius: 8,
                                border: 'none',
                                cursor: has ? 'pointer' : 'default',
                                background: sel ? '#6366f1' : '#f1f5f9',
                                color: sel ? '#fff' : has ? '#475569' : '#cbd5e1',
                                fontWeight: sel ? 800 : 600,
                                fontSize: 13,
                            }}
                        >
                            {sel ? '★' : '☆'}
                            {s}
                        </button>
                    );
                })}
            </div>

            {data ? (
                <>
                    {data.summary && (
                        <div
                            style={{
                                fontSize: 12,
                                fontStyle: 'italic',
                                color: '#64748b',
                                marginBottom: 16,
                                padding: '8px 12px',
                                background: '#f8fafc',
                                borderRadius: 8,
                            }}
                        >
                            &ldquo;{data.summary}&rdquo;
                        </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div
                            style={{
                                background: '#f0fdf4',
                                padding: 14,
                                borderRadius: 10,
                                border: '1px solid #bbf7d0',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: '#10b981',
                                    marginBottom: 10,
                                }}
                            >
                                장점
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {data.pros?.length ? (
                                    data.pros.map(p => (
                                        <Tag key={p} color="green" style={{ margin: 0 }}>
                                            {p}
                                        </Tag>
                                    ))
                                ) : (
                                    <span style={{ color: '#94a3b8', fontSize: 12 }}>
                                        해당 없음
                                    </span>
                                )}
                            </div>
                        </div>
                        <div
                            style={{
                                background: '#fef2f2',
                                padding: 14,
                                borderRadius: 10,
                                border: '1px solid #fecaca',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: '#ef4444',
                                    marginBottom: 10,
                                }}
                            >
                                단점
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {data.cons?.length ? (
                                    data.cons.map(c => (
                                        <Tag key={c} color="red" style={{ margin: 0 }}>
                                            {c}
                                        </Tag>
                                    ))
                                ) : (
                                    <span style={{ color: '#94a3b8', fontSize: 12 }}>
                                        해당 없음
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <div style={{ color: '#94a3b8', fontSize: 13 }}>해당 별점 리뷰 데이터 없음</div>
            )}
        </div>
    );
};

// 날짜별 평균 평점 계산
function calcRatingTrend(reviews) {
    if (!reviews || reviews.length === 0) return { series: [], weekly: null, monthly: null };
    const byDate = {};
    reviews.forEach(r => {
        const d = r.createDate?.slice(0, 10);
        if (!d || !r.score) return;
        if (!byDate[d]) byDate[d] = [];
        byDate[d].push(r.score);
    });
    const series = Object.entries(byDate)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, scores]) => ({
            date,
            avg: Math.round((scores.reduce((s, v) => s + v, 0) / scores.length) * 100) / 100,
            count: scores.length,
        }));

    if (series.length === 0) return { series, weekly: null, monthly: null };

    const lastDate = new Date(series[series.length - 1].date);
    const d7 = new Date(lastDate);
    d7.setDate(d7.getDate() - 7);
    const d14 = new Date(lastDate);
    d14.setDate(d14.getDate() - 14);
    const d30 = new Date(lastDate);
    d30.setDate(d30.getDate() - 30);
    const d60 = new Date(lastDate);
    d60.setDate(d60.getDate() - 60);

    const avgOf = (from, to) => {
        const pts = series.filter(s => {
            const dt = new Date(s.date);
            return dt >= from && dt <= to;
        });
        if (!pts.length) return null;
        const allScores = reviews.filter(r => {
            const dt = new Date(r.createDate?.slice(0, 10));
            return dt >= from && dt <= to;
        });
        if (!allScores.length) return null;
        return (
            Math.round((allScores.reduce((s, r) => s + r.score, 0) / allScores.length) * 100) / 100
        );
    };

    const curWeek = avgOf(d7, lastDate);
    const prevWeek = avgOf(d14, d7);
    const curMonth = avgOf(d30, lastDate);
    const prevMonth = avgOf(d60, d30);

    return {
        series,
        weekly:
            curWeek !== null
                ? {
                      value: curWeek,
                      change:
                          prevWeek !== null ? Math.round((curWeek - prevWeek) * 100) / 100 : null,
                  }
                : null,
        monthly:
            curMonth !== null
                ? {
                      value: curMonth,
                      change:
                          prevMonth !== null
                              ? Math.round((curMonth - prevMonth) * 100) / 100
                              : null,
                  }
                : null,
    };
}

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div
            style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 12,
                boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
            }}
        >
            <div style={{ color: '#64748b', marginBottom: 2 }}>{label}</div>
            <div style={{ fontWeight: 800, color: '#6366f1' }}>★ {payload[0].value}</div>
        </div>
    );
};

const ReviewAnalysisSection = ({ report }) => {
    const reviewData = report?.reviewData;
    const ai = reviewData?.aiReviewAnalysis;
    const summaryInfo = reviewData?.reviewSummary?.productReviewInfo;
    const amount = reviewData?.reviewAmount;

    const {
        series: ratingTrend,
        weekly,
        monthly,
    } = useMemo(() => calcRatingTrend(reviewData?.reviews), [reviewData]);

    const totalCount = amount?.totalReviewCount || 0;
    const avgScore = amount?.averageReviewScore || 0;
    const photoCount = summaryInfo?.photoReviewCount || 0;
    const photoRatioPct = totalCount > 0 ? Math.round((photoCount / totalCount) * 100) : 0;
    const score1 = summaryInfo?.score1ReviewCount || 0;
    const score2 = summaryInfo?.score2ReviewCount || 0;
    const negRatioPct =
        totalCount > 0 ? Math.round(((score1 + score2) / totalCount) * 1000) / 10 : 0;
    const analyzedCount = reviewData?.reviews?.length || 0;

    const hasData = !!reviewData;
    const hasAI = !!ai;

    return (
        <>
            <div className="section-title-premium">
                <CommentOutlined /> 리뷰 심층 분석
                <span
                    style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 'auto' }}
                >
                    {totalCount > 0
                        ? `총 ${totalCount.toLocaleString()}개 · AI 분석 ${analyzedCount}건`
                        : '리뷰 데이터 없음'}
                </span>
            </div>

            {!hasData && (
                <div
                    style={{
                        background: '#fffbeb',
                        border: '1px solid #fbbf24',
                        borderRadius: 8,
                        padding: '10px 16px',
                        marginBottom: 16,
                        fontSize: 12,
                        color: '#92400e',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <AlertFilled style={{ color: '#f59e0b' }} /> 상품 분석을 먼저 실행해주세요.
                </div>
            )}
            {hasData && !hasAI && (
                <div
                    style={{
                        background: '#f0f9ff',
                        border: '1px solid #bae6fd',
                        borderRadius: 8,
                        padding: '10px 16px',
                        marginBottom: 16,
                        fontSize: 12,
                        color: '#0369a1',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <LoadingOutlined /> AI 리뷰 분석 데이터가 없습니다. 다시 분석을 실행해주세요.
                </div>
            )}

            {/* 상단 요약 카드 5개 */}
            <Row gutter={12} style={{ marginBottom: 20 }}>
                {[
                    {
                        icon: <StarFilled />,
                        label: '평균 평점',
                        value: avgScore > 0 ? `${avgScore.toFixed(2)}점` : '-',
                        cls: 'c-orange',
                    },
                    {
                        icon: <MessageOutlined />,
                        label: '전체 리뷰',
                        value: totalCount > 0 ? totalCount.toLocaleString() : '-',
                        cls: 'c-blue',
                    },
                    {
                        icon: <RobotOutlined />,
                        label: 'AI 분석 건수',
                        value: analyzedCount > 0 ? `${analyzedCount}건` : '-',
                        cls: 'c-purple',
                    },
                    {
                        icon: <CameraOutlined />,
                        label: '포토 리뷰',
                        value: totalCount > 0 ? `${photoRatioPct}%` : '-',
                        cls: 'c-pink',
                    },
                    {
                        icon: <AlertFilled />,
                        label: '불만 비율',
                        value: totalCount > 0 ? `${negRatioPct}%` : '-',
                        cls: 'c-red',
                    },
                ].map((s, i) => (
                    <Col key={i} flex="1">
                        <div className={`summary-card ${s.cls}`} style={{ padding: '18px 16px' }}>
                            <div className="header">
                                <div className="icon-wrap">{s.icon}</div>
                                <div className="label">{s.label}</div>
                            </div>
                            <div className="value">{s.value}</div>
                        </div>
                    </Col>
                ))}
            </Row>

            {/* 평점 추이 차트 */}
            {ratingTrend.length > 0 && (
                <div className="diag-card" style={{ padding: 24, marginBottom: 16 }}>
                    <Row gutter={16} align="middle">
                        <Col flex={1} style={{ minWidth: 0 }}>
                            <div
                                style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: '#6366f1',
                                    marginBottom: 16,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                <HistoryOutlined /> 평점 추이
                            </div>
                            <ResponsiveContainer width="100%" height={160}>
                                <LineChart
                                    data={ratingTrend}
                                    margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 10, fill: '#94a3b8' }}
                                        tickFormatter={v => v.slice(5)}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis
                                        domain={[1, 5]}
                                        ticks={[1, 2, 3, 4, 5]}
                                        tick={{ fontSize: 10, fill: '#94a3b8' }}
                                    />
                                    <ReferenceLine y={5} stroke="#e2e8f0" strokeDasharray="4 4" />
                                    <ReferenceLine y={2} stroke="#fecaca" strokeDasharray="4 4" />
                                    <RechartsTooltip content={<CustomTooltip />} />
                                    <Line
                                        type="monotone"
                                        dataKey="avg"
                                        stroke="#6366f1"
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 4, fill: '#6366f1' }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Col>
                        <Col style={{ width: 140, flexShrink: 0 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {[
                                    {
                                        label: '주간 변동',
                                        data: weekly,
                                        tip: '최근 7일 평균 평점 vs 이전 7일 평균 평점의 차이',
                                    },
                                    {
                                        label: '월간 변동',
                                        data: monthly,
                                        tip: '최근 30일 평균 평점 vs 이전 30일 평균 평점의 차이',
                                    },
                                ].map(({ label, data, tip }) => (
                                    <div
                                        key={label}
                                        className="diag-card"
                                        style={{ padding: '14px 16px' }}
                                    >
                                        <Tooltip title={tip} placement="left">
                                            <div
                                                style={{
                                                    fontSize: 11,
                                                    color: '#9ca3af',
                                                    marginBottom: 4,
                                                    cursor: 'help',
                                                    borderBottom: '1px dashed #e2e8f0',
                                                    paddingBottom: 4,
                                                }}
                                            >
                                                {label} ⓘ
                                            </div>
                                        </Tooltip>
                                        {data ? (
                                            <>
                                                <div
                                                    style={{
                                                        fontSize: 20,
                                                        fontWeight: 800,
                                                        color: '#0f172a',
                                                    }}
                                                >
                                                    {data.value.toFixed(2)}
                                                </div>
                                                {data.change !== null && (
                                                    <div
                                                        style={{
                                                            fontSize: 12,
                                                            fontWeight: 700,
                                                            color:
                                                                data.change >= 0
                                                                    ? '#10b981'
                                                                    : '#ef4444',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                        }}
                                                    >
                                                        {data.change >= 0 ? (
                                                            <RiseOutlined />
                                                        ) : (
                                                            <FallOutlined />
                                                        )}
                                                        {data.change >= 0 ? '+' : ''}
                                                        {data.change.toFixed(2)}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div style={{ fontSize: 12, color: '#94a3b8' }}>
                                                데이터 부족
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                </div>
            )}

            {/* 3열 메인 레이아웃 */}
            <div className="diagnostic-card-group">
                {/* 왼쪽: 별점 분포 + 최근 리뷰 샘플 */}
                <div className="diag-card">
                    <div
                        style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#6366f1',
                            marginBottom: 20,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                        }}
                    >
                        <StarFilled style={{ color: '#f59e0b' }} /> 별점 분포
                    </div>
                    {summaryInfo ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {[5, 4, 3, 2, 1].map(star => {
                                const cnt = summaryInfo[`score${star}ReviewCount`] || 0;
                                const pct =
                                    totalCount > 0 ? Math.round((cnt / totalCount) * 100) : 0;
                                const color =
                                    star >= 4 ? '#10b981' : star === 3 ? '#f59e0b' : '#ef4444';
                                return (
                                    <div
                                        key={star}
                                        style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 12,
                                                color: '#64748b',
                                                width: 20,
                                                textAlign: 'right',
                                                flexShrink: 0,
                                            }}
                                        >
                                            ★{star}
                                        </span>
                                        <div style={{ flex: 1 }}>
                                            <Progress
                                                percent={pct}
                                                strokeColor={color}
                                                trailColor="#f1f5f9"
                                                showInfo={false}
                                                size={['100%', 10]}
                                            />
                                        </div>
                                        <span
                                            style={{
                                                fontSize: 12,
                                                color: '#64748b',
                                                width: 42,
                                                textAlign: 'right',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {cnt.toLocaleString()}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: 11,
                                                color: '#94a3b8',
                                                width: 28,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {pct}%
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{ color: '#94a3b8', fontSize: 13 }}>분포 데이터 없음</div>
                    )}

                    {/* 최근 리뷰 샘플 */}
                    {reviewData?.reviews?.length > 0 && (
                        <>
                            <div
                                style={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: '#6366f1',
                                    margin: '24px 0 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                <CommentOutlined /> 최근 리뷰 샘플
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {reviewData.reviews.slice(0, 4).map(r => (
                                    <div
                                        key={r.id}
                                        style={{
                                            background: '#f8fafc',
                                            borderRadius: 12,
                                            padding: '12px 14px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                marginBottom: 6,
                                            }}
                                        >
                                            <Space size={1}>
                                                {[1, 2, 3, 4, 5].map(s => (
                                                    <StarFilled
                                                        key={s}
                                                        style={{
                                                            fontSize: 10,
                                                            color:
                                                                s <= r.score
                                                                    ? '#f59e0b'
                                                                    : '#e2e8f0',
                                                        }}
                                                    />
                                                ))}
                                            </Space>
                                            {r.hasPhoto && (
                                                <Tag
                                                    color="pink"
                                                    style={{
                                                        fontSize: 10,
                                                        padding: '0 4px',
                                                        margin: 0,
                                                    }}
                                                >
                                                    포토
                                                </Tag>
                                            )}
                                            {r.repurchase && (
                                                <Tag
                                                    color="green"
                                                    style={{
                                                        fontSize: 10,
                                                        padding: '0 4px',
                                                        margin: 0,
                                                    }}
                                                >
                                                    재구매
                                                </Tag>
                                            )}
                                            <span
                                                style={{
                                                    fontSize: 10,
                                                    color: '#94a3b8',
                                                    marginLeft: 'auto',
                                                }}
                                            >
                                                {r.createDate?.slice(0, 10)}
                                            </span>
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 12,
                                                color: '#475569',
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            {r.content?.slice(0, 100)}
                                            {r.content?.length > 100 ? '...' : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* 가운데: AI 긍정/부정 핵심 + 한줄 요약 */}
                <div className="diag-card">
                    <div
                        style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#6366f1',
                            marginBottom: 20,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                        }}
                    >
                        <RobotOutlined /> AI 리뷰 인사이트
                        <span
                            style={{
                                fontSize: 10,
                                background: '#e0e7ff',
                                color: '#4f46e5',
                                borderRadius: 20,
                                padding: '1px 8px',
                                fontWeight: 700,
                            }}
                        >
                            AI
                        </span>
                    </div>

                    <div
                        style={{
                            background: '#f0fdf4',
                            border: '1px solid #bbf7d0',
                            borderRadius: 14,
                            padding: 16,
                            marginBottom: 14,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                fontSize: 13,
                                fontWeight: 700,
                                color: '#10b981',
                                marginBottom: 12,
                            }}
                        >
                            <SmileOutlined /> 긍정 핵심
                        </div>
                        <div style={{ fontSize: 13, color: '#065f46', lineHeight: 2 }}>
                            {hasAI ? (
                                ai.positivePoints?.map((p, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 8 }}>
                                        <span style={{ color: '#10b981', flexShrink: 0 }}>✓</span>
                                        <span>{p}</span>
                                    </div>
                                ))
                            ) : (
                                <span style={{ color: '#94a3b8' }}>분석 대기 중</span>
                            )}
                        </div>
                    </div>

                    <div
                        style={{
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: 14,
                            padding: 16,
                            marginBottom: 14,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                fontSize: 13,
                                fontWeight: 700,
                                color: '#ef4444',
                                marginBottom: 12,
                            }}
                        >
                            <FrownOutlined /> 부정 핵심
                        </div>
                        <div style={{ fontSize: 13, color: '#991b1b', lineHeight: 2 }}>
                            {hasAI ? (
                                ai.negativePoints?.map((p, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 8 }}>
                                        <span style={{ color: '#ef4444', flexShrink: 0 }}>✗</span>
                                        <span>{p}</span>
                                    </div>
                                ))
                            ) : (
                                <span style={{ color: '#94a3b8' }}>분석 대기 중</span>
                            )}
                        </div>
                    </div>

                    {hasAI && ai.summary && (
                        <div
                            style={{
                                background: '#f5f3ff',
                                border: '1px solid #ddd6fe',
                                borderRadius: 14,
                                padding: 16,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: '#7c3aed',
                                    marginBottom: 6,
                                }}
                            >
                                💬 AI 종합 요약
                            </div>
                            <div style={{ fontSize: 13, color: '#5b21b6', lineHeight: 1.7 }}>
                                {ai.summary}
                            </div>
                        </div>
                    )}
                </div>

                {/* 오른쪽: TOP 키워드 + 주요 불만 */}
                <div
                    className="diag-card"
                    style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
                >
                    <div>
                        <div
                            style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: '#3b82f6',
                                marginBottom: 16,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                            }}
                        >
                            # 리뷰 TOP 키워드
                        </div>
                        {hasAI && ai.topKeywords?.length ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {ai.topKeywords.map((x, _i) => {
                                    const maxCnt = ai.topKeywords[0]?.count || 1;
                                    const pct = Math.round((x.count / maxCnt) * 100);
                                    return (
                                        <div key={x.keyword}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    fontSize: 13,
                                                    marginBottom: 4,
                                                }}
                                            >
                                                <span style={{ color: '#334155', fontWeight: 600 }}>
                                                    {x.keyword}
                                                </span>
                                                <span style={{ color: '#3b82f6', fontWeight: 800 }}>
                                                    {x.count}
                                                </span>
                                            </div>
                                            <Progress
                                                percent={pct}
                                                strokeColor="#3b82f6"
                                                trailColor="#eff6ff"
                                                showInfo={false}
                                                size={['100%', 6]}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{ color: '#94a3b8', fontSize: 13 }}>분석 대기 중</div>
                        )}
                    </div>

                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
                        <div
                            style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: '#f59e0b',
                                marginBottom: 16,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                            }}
                        >
                            ⚠️ 주요 불만 키워드
                        </div>
                        {hasAI && ai.complaintKeywords?.length ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {ai.complaintKeywords.map((x, _i) => {
                                    const maxCnt = ai.complaintKeywords[0]?.count || 1;
                                    const pct = Math.round((x.count / maxCnt) * 100);
                                    return (
                                        <div key={x.keyword}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    fontSize: 13,
                                                    marginBottom: 4,
                                                }}
                                            >
                                                <span style={{ color: '#334155', fontWeight: 600 }}>
                                                    {x.keyword}
                                                </span>
                                                <span style={{ color: '#f59e0b', fontWeight: 800 }}>
                                                    {x.count}
                                                </span>
                                            </div>
                                            <Progress
                                                percent={pct}
                                                strokeColor="#f59e0b"
                                                trailColor="#fffbeb"
                                                showInfo={false}
                                                size={['100%', 6]}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{ color: '#94a3b8', fontSize: 13 }}>분석 대기 중</div>
                        )}
                    </div>
                </div>
            </div>

            {/* 레이더 차트 + 별점별 분석 */}
            {hasAI && (ai.topicScores || ai.starAnalysis) && (
                <div className="diagnostic-card-group" style={{ marginTop: 16 }}>
                    {/* 고객 만족 토픽 레이더 */}
                    {ai.topicScores && (
                        <div
                            className="diag-card"
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: '#6366f1',
                                    marginBottom: 8,
                                    alignSelf: 'flex-start',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                <PieChartOutlined /> 고객 만족 토픽
                            </div>
                            <RadarChart
                                width={260}
                                height={220}
                                data={Object.entries(ai.topicScores).map(([subject, value]) => ({
                                    subject,
                                    value,
                                }))}
                            >
                                <PolarGrid stroke="#e2e8f0" />
                                <PolarAngleAxis
                                    dataKey="subject"
                                    tick={{ fontSize: 12, fill: '#64748b', fontWeight: 600 }}
                                />
                                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                                <Radar
                                    dataKey="value"
                                    stroke="#6366f1"
                                    fill="#6366f1"
                                    fillOpacity={0.25}
                                    strokeWidth={2}
                                />
                            </RadarChart>
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 8,
                                    justifyContent: 'center',
                                    marginTop: 4,
                                }}
                            >
                                {Object.entries(ai.topicScores).map(([k, v]) => (
                                    <div key={k} style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{k}</div>
                                        <div
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 800,
                                                color:
                                                    v >= 80
                                                        ? '#10b981'
                                                        : v >= 60
                                                          ? '#f59e0b'
                                                          : '#ef4444',
                                            }}
                                        >
                                            {v}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 별점별 분석 */}
                    {ai.starAnalysis && (
                        <div
                            className="diag-card"
                            style={{ gridColumn: ai.topicScores ? 'span 2' : 'span 3' }}
                        >
                            <div
                                style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: '#f59e0b',
                                    marginBottom: 16,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                <StarFilled style={{ color: '#f59e0b' }} /> 평점별 분석
                            </div>
                            <StarAnalysisTabs starAnalysis={ai.starAnalysis} />
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default ReviewAnalysisSection;
